import React from 'react';
import LeadershipChrome from '@/components/LeadershipChrome';
import ReceptionLayout from '@/components/ReceptionLayout';
import DoctorCampusShell from '@/components/DoctorCampusShell';
import PharmacyCampusShell from '@/components/PharmacyCampusShell';
import LeadershipPageShell from '@/components/LeadershipPageShell';
import { useAuth } from '@/contexts/AuthContext';
import { LEADERSHIP_CONTENT } from '@/config/leadershipLayout';
import { PURCHASE_MENU } from '@/config/navigationMenus';

const RECEPTION_ROLES = new Set(['receptiondesk', 'staff_nurse', 'multi_skill_assistant']);
const PHARMACY_ROLES = new Set(['pharmacist', 'pharmacy']);
const LEADERSHIP_ROLES = new Set(['fic', 'management', 'director', 'dean', 'hospital_director']);

/**
 * Pick FIC-style campus chrome from the signed-in role.
 * Clinic /admin keeps its own sidebar — callers should not wrap admin.
 */
export default function CampusRoleShell({
  children,
  pageTitle,
  pageSubtitle,
  rightActions,
  homeHref,
}) {
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();

  if (role === 'admin') {
    return children;
  }

  if (RECEPTION_ROLES.has(role)) {
    return (
      <ReceptionLayout pageTitle={pageTitle} pageSubtitle={pageSubtitle} rightActions={rightActions}>
        {children}
      </ReceptionLayout>
    );
  }

  if (role === 'doctor') {
    return (
      <DoctorCampusShell>
        {(pageTitle || rightActions) ? (
          <CampusPageHead title={pageTitle} subtitle={pageSubtitle} actions={rightActions} />
        ) : null}
        {children}
      </DoctorCampusShell>
    );
  }

  if (PHARMACY_ROLES.has(role)) {
    return (
      <PharmacyCampusShell pageTitle={pageTitle} pageSubtitle={pageSubtitle} rightActions={rightActions}>
        {children}
      </PharmacyCampusShell>
    );
  }

  if (LEADERSHIP_ROLES.has(role)) {
    return (
      <LeadershipPageShell title={pageTitle} subtitle={pageSubtitle} headerActions={rightActions}>
        {children}
      </LeadershipPageShell>
    );
  }

  if (role === 'purchase') {
    return (
      <LeadershipChrome
        homeHref={homeHref || '/purchase'}
        roleLabel="Purchase"
        menuItems={PURCHASE_MENU}
        actions={rightActions}
      >
        <div className={LEADERSHIP_CONTENT}>
          {pageTitle ? <CampusPageHead title={pageTitle} subtitle={pageSubtitle} /> : null}
          {children}
        </div>
      </LeadershipChrome>
    );
  }

  return (
    <LeadershipChrome homeHref={homeHref || '/'} roleLabel={role || 'Staff'} menuItems={[]}>
      <div className={LEADERSHIP_CONTENT}>
        {pageTitle ? <CampusPageHead title={pageTitle} subtitle={pageSubtitle} actions={rightActions} /> : null}
        {children}
      </div>
    </LeadershipChrome>
  );
}

function CampusPageHead({ title, subtitle, actions }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
      <div>
        {title ? <h1 className="text-2xl font-semibold text-[#071B34] font-heading">{title}</h1> : null}
        {subtitle ? <p className="text-sm text-[#5A6B7C] mt-1">{subtitle}</p> : null}
      </div>
      {actions}
    </div>
  );
}
